import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PostCreationData } from '@openpeepshq/common/types';
import {
  useT,
  useOpenpeeps,
  defaultNewArticle,
  getNewPostStores,
  useSetPageHeader,
} from '../../index';
import { ArticleForm, useServerInfo } from '../../components';
import { Button, Toast } from '@openpeepshq/react-ui';

export function NewArticle() {
  const t = useT();
  const navigate = useNavigate();
  const serverInfo = useServerInfo();
  const { openpeepsApi } = useOpenpeeps();
  const createPost = openpeepsApi.createPostAction();
  const stores = getNewPostStores();

  const [postData, setPostData] = useState<PostCreationData>(() => {
    const stored = stores.article;
    if (stored.type === 'article' && stored.data.type === 'article') {
      return stored;
    }
    return defaultNewArticle(serverInfo.publicContent);
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const article = postData.data.type === 'article' ? postData.data : null;
  const canSubmit =
    !!article?.title?.trim() &&
    !(postData.visibility === 'direct' && !postData.audience?.length);

  const submit = useCallback(async () => {
    setError(null);
    if (!article?.title?.trim()) {
      setError(
        t('articles.validation.titleRequired', {
          defaultValue: 'Title is required',
        }),
      );
      return;
    }
    if (postData.visibility === 'direct' && !postData.audience?.length) {
      setError('Choose at least one recipient for a direct article.');
      return;
    }
    setSubmitting(true);
    try {
      const created = await createPost({ ...postData, type: 'article' });
      stores.resetNewArticleState();
      navigate(`/posts/${created.id}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }, [article, createPost, navigate, postData, stores, t]);

  const headerActions = useMemo(
    () => (
      <Button
        title={t('articles.create.title', { defaultValue: 'Create article' })}
        variant="default"
        action={submit}
        disabled={submitting || !canSubmit}
      >
        {submitting
          ? t('common.submitting', { defaultValue: 'Publishing…' })
          : t('articles.create.title', { defaultValue: 'Create article' })}
      </Button>
    ),
    [canSubmit, submit, submitting, t],
  );

  useSetPageHeader(
    t('articles.new', { defaultValue: 'New article' }),
    headerActions,
  );

  return (
    <div className="pb-12">
      <ArticleForm
        postData={postData}
        onChange={(data) => {
          setPostData(data);
          stores.article = data;
        }}
      />

      {error ? (
        <div className="px-3">
          <Toast variant="error" onDismiss={() => setError(null)}>
            {error}
          </Toast>
        </div>
      ) : null}
    </div>
  );
}
